// src/hooks/usePortfolioHealth.js - FIXED with Smart Analysis Caching
// ✅ FIXED: Analysis button now opens existing results, refresh button generates new analysis

import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { calculateProfileCompleteness } from '../utils/profileUtils';
import logger from '../utils/logger';

// Module-level session cache — survives component unmounts during navigation
// Cleared only on full page reload or after 1 hour
const _sessionCache = {
  healthData: null,
  newsData: null,
  rateLimitInfo: null,
  newsRateLimitInfo: null,
  lastAnalysisTime: null,
  lastNewsAnalysisTime: null,
  userId: null, // track which user this cache belongs to
};

const CACHE_TTL = 60 * 60 * 1000; // 1 hour

function _getCached(userId) {
  if (_sessionCache.userId !== userId) return null;
  if (_sessionCache.lastAnalysisTime && (Date.now() - _sessionCache.lastAnalysisTime) > CACHE_TTL) {
    // Expired — clear health cache (keep news separate)
    _sessionCache.healthData = null;
    _sessionCache.lastAnalysisTime = null;
    _sessionCache.rateLimitInfo = null;
  }
  if (_sessionCache.lastNewsAnalysisTime && (Date.now() - _sessionCache.lastNewsAnalysisTime) > CACHE_TTL) {
    _sessionCache.newsData = null;
    _sessionCache.lastNewsAnalysisTime = null;
    _sessionCache.newsRateLimitInfo = null;
  }
  return _sessionCache;
}

function _updateCache(userId, updates) {
  _sessionCache.userId = userId;
  Object.assign(_sessionCache, updates);
}

const usePortfolioHealth = (user, userProfile) => {
  // Initialize state from session cache if available for this user
  const cached = _getCached(user?.uid);
  const [healthData, setHealthData] = useState(cached?.healthData || null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [shouldAutoShow, setShouldAutoShow] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState(cached?.rateLimitInfo || null);
  const [error, setError] = useState(null);

  // News analysis state
  const [newsData, setNewsData] = useState(cached?.newsData || null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState(null);
  const [newsRateLimitInfo, setNewsRateLimitInfo] = useState(cached?.newsRateLimitInfo || null);

  // Track when analysis was last generated
  const [lastAnalysisTime, setLastAnalysisTime] = useState(cached?.lastAnalysisTime || null);
  const [lastNewsAnalysisTime, setLastNewsAnalysisTime] = useState(cached?.lastNewsAnalysisTime || null);

  // Helper function to check if user qualifies for health report
  const qualifiesForReport = useCallback((profile) => {
    if (!profile) return false;
    const completeness = calculateProfileCompleteness(profile);
    const hasHoldings = profile.holdings && profile.holdings.length >= 2;
    return completeness >= 50 && hasHoldings;
  }, []);

  // Check if this is first visit today
  const isFirstVisitToday = useCallback(() => {
    const today = new Date().toDateString();
    const lastReportView = localStorage.getItem(`lastHealthReport_${user?.uid}`);
    return lastReportView !== today;
  }, [user?.uid]);

  // Check if profile just reached qualification for first time
  const isFirstTimeQualified = useCallback((profile) => {
    const qualificationKey = `portfolioQualified_${user?.uid}`;
    const hasQualifiedBefore = localStorage.getItem(qualificationKey);
    
    if (qualifiesForReport(profile) && !hasQualifiedBefore) {
      localStorage.setItem(qualificationKey, 'true');
      return true;
    }
    
    return false;
  }, [user?.uid, qualifiesForReport]);

  // ✅ FIXED: Function to generate NEW portfolio health report (only called by refresh)
  const generateNewReport = useCallback(async () => {
    if (!qualifiesForReport(userProfile)) {
      throw new Error('Profile not complete enough for portfolio analysis');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/v1/portfolio/health-check', {
        holdings: userProfile.holdings,
        user_profile: {
          ...userProfile,
          isPremium: user?.isPremium || false
        },
        user_id: user?.uid || null
      });

      const now = Date.now();
      setHealthData(response.data);
      setLastAnalysisTime(now);

      // Update usage info
      if (response.data.usage) {
        setRateLimitInfo(response.data.usage);
      }

      // Persist to session cache so data survives navigation
      _updateCache(user?.uid, {
        healthData: response.data,
        lastAnalysisTime: now,
        rateLimitInfo: response.data.usage || null,
      });

      // Mark as viewed today
      if (user?.uid) {
        const today = new Date().toDateString();
        localStorage.setItem(`lastHealthReport_${user.uid}`, today);
      }

      setShouldAutoShow(false);
      return response.data;
    } catch (error) {
      console.error('Error generating portfolio health report:', error);
      
      if (error.response?.status === 429) {
        const errorData = error.response.data.detail;
        setError(errorData.message || 'Daily limit reached');
      } else {
        setError(error.message || 'Failed to generate portfolio health report');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, [qualifiesForReport, userProfile, user?.uid, user?.isPremium]);

  // ✅ FIXED: Function to generate NEW news analysis (only called by refresh)
  const generateNewNewsAnalysis = useCallback(async () => {
    if (!qualifiesForReport(userProfile)) {
      throw new Error('Portfolio not ready for news analysis - need at least 2 holdings');
    }

    setNewsLoading(true);
    setNewsError(null);

    try {
      const response = await api.post('/v1/portfolio/news-analysis', {
        holdings: userProfile.holdings,
        user_profile: {
          ...userProfile,
          isPremium: user?.isPremium || false
        },
        user_id: user?.uid || null
      });

      const now = Date.now();
      setNewsData(response.data);
      setLastNewsAnalysisTime(now);

      // Update news usage info
      if (response.data.usage) {
        setNewsRateLimitInfo(response.data.usage);
      }

      // Persist to session cache
      _updateCache(user?.uid, {
        newsData: response.data,
        lastNewsAnalysisTime: now,
        newsRateLimitInfo: response.data.usage || null,
      });

      return response.data;
    } catch (error) {
      console.error('Error generating news analysis:', error);
      
      if (error.response?.status === 429) {
        const errorData = error.response.data.detail;
        setNewsError(errorData.message || 'Weekly news analysis limit reached');
        
        if (errorData) {
          setNewsRateLimitInfo({
            remaining_analyses: errorData.remaining || 0,
            weekly_limit: errorData.weekly_limit || 1,
            current_tier: errorData.current_tier || 'free',
            reset_date: errorData.reset_date
          });
        }
      } else if (error.response?.status === 400) {
        const errorData = error.response.data.detail;
        setNewsError(errorData.message || 'Insufficient holdings for news analysis');
      } else {
        setNewsError(error.message || 'Failed to generate news analysis');
      }
      throw error;
    } finally {
      setNewsLoading(false);
    }
  }, [qualifiesForReport, userProfile, user?.uid, user?.isPremium]);

  // ✅ NEW: Function to check news analysis usage
  const checkNewsUsage = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const response = await api.get(`/v1/portfolio/news-analysis/usage/${user.uid}`);
      setNewsRateLimitInfo(response.data);
      return response.data;
    } catch (error) {
      console.error('Error checking news usage:', error);
    }
  }, [user?.uid]);

  // Load news usage info when user or profile changes
  useEffect(() => {
    if (user?.uid && qualifiesForReport(userProfile)) {
      checkNewsUsage();
    }
  }, [user?.uid, userProfile, checkNewsUsage, qualifiesForReport]);

  // Main logic to determine if we should auto-show the report (only on first qualification)
  useEffect(() => {
    if (!userProfile) return;

    const shouldShow = 
      (isFirstVisitToday() && qualifiesForReport(userProfile)) ||
      isFirstTimeQualified(userProfile);

    if (shouldShow) {
      setShouldAutoShow(true);
      
      // Auto-generate after a short delay
      const timeoutId = setTimeout(() => {
        generateNewReport().catch(console.error);
        setShowModal(true); // ✅ Show modal after generating
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [userProfile, isFirstVisitToday, qualifiesForReport, isFirstTimeQualified, generateNewReport]);

  // ✅ NEW: Smart function to open analysis (doesn't regenerate if exists)
  const openAnalysis = useCallback(async () => {
    try {
      // If we have existing health data, just show the modal
      if (healthData && lastAnalysisTime) {
        logger.log('📊 Opening existing portfolio analysis');
        setShowModal(true);
        return healthData;
      }

      // If no existing data, generate new analysis
      logger.log('📊 Generating new portfolio analysis');
      const newData = await generateNewReport();
      setShowModal(true);
      return newData;
    } catch (error) {
      console.error('Failed to open analysis:', error);
      // Still show modal even if generation failed, so user sees error
      setShowModal(true);
    }
  }, [healthData, lastAnalysisTime, generateNewReport]);

  // ✅ NEW: Smart function to open news analysis (doesn't regenerate if exists)
  const openNewsAnalysis = useCallback(async () => {
    try {
      // If we have existing news data, just show it
      if (newsData && lastNewsAnalysisTime) {
        logger.log('📰 Opening existing news analysis');
        setShowModal(true);
        return newsData;
      }

      // If no existing data, generate new analysis
      logger.log('📰 Generating new news analysis');
      const newData = await generateNewNewsAnalysis();
      setShowModal(true);
      return newData;
    } catch (error) {
      console.error('Failed to open news analysis:', error);
      // Still show modal even if generation failed
      setShowModal(true);
    }
  }, [newsData, lastNewsAnalysisTime, generateNewNewsAnalysis]);

  // ✅ FIXED: Function to manually trigger NEW report (for refresh button only)
  const triggerReport = useCallback(async () => {
    try {
      await generateNewReport();
    } catch (error) {
      console.error('Failed to generate report:', error);
    }
  }, [generateNewReport]);

  // ✅ FIXED: Function to manually trigger NEW news analysis (for refresh button only)
  const triggerNewsAnalysis = useCallback(async () => {
    try {
      await generateNewNewsAnalysis();
    } catch (error) {
      console.error('Failed to generate news analysis:', error);
    }
  }, [generateNewNewsAnalysis]);

  // Function to dismiss the modal
  const dismissModal = useCallback(() => {
    setShowModal(false);
    setError(null);
    setNewsError(null);
  }, []);

  // Function to get health score color
  const getHealthScoreColor = useCallback((score) => {
    if (score >= 80) return '#4CAF50'; // Green
    if (score >= 60) return '#FF9800'; // Orange
    return '#F44336'; // Red
  }, []);

  // Function to format health data for chat display
  const formatHealthForChat = useCallback((data, currencyFormatter) => {
    if (!data) return '';
    
    // Use provided currency formatter or fallback to USD formatting
    const formatValue = currencyFormatter || ((amount) => `$${amount.toLocaleString()}`);

    return `## 📊 Portfolio Health Report

**Overall Health Score: ${data.health_score}/100**

### 📈 Key Metrics
- Portfolio Value: ${formatValue(data.portfolio_value)}
- Total Return: ${data.total_return_percentage.toFixed(1)}%
- Risk Level: ${data.risk_analysis.risk_level}
- Diversification Score: ${data.sector_allocation.diversification_score}/100

### 💡 Top Insights
${data.insights.map(insight => `- ${insight.icon} **${insight.title}**: ${insight.message}`).join('\n')}

### 🎯 Recommendations
${data.recommendations.slice(0, 3).map(rec => `- ${rec.icon} **${rec.title}** (${rec.priority}): ${rec.description}`).join('\n')}

### 🏭 Sector Allocation
${data.sector_allocation.sectors.slice(0, 5).map(sector => `- ${sector.name}: ${sector.percentage}%`).join('\n')}

*Analysis generated: ${new Date(data.generated_at).toLocaleString()}*

Would you like me to explain any of these metrics or provide more detailed recommendations?`;
  }, []);

  // Function to format news data for chat display
  const formatNewsForChat = useCallback((data) => {
    if (!data || !data.report) return '';

    const report = data.report;

    return `## 📰 Portfolio News Analysis

**Market Sentiment: ${report.market_sentiment || 'Neutral'}**
**Holdings Analyzed: ${data.holdings_analyzed || 0}**
**Articles Reviewed: ${data.total_articles || 0}**

${report.formatted_report || 'News analysis completed successfully.'}

*Analysis generated: ${new Date().toLocaleString()}*

Would you like me to explain any specific news impacts or provide more details about particular holdings?`;
  }, []);

  // ✅ NEW: Helper function to check if analysis is recent (less than 1 hour old)
  const isAnalysisRecent = useCallback(() => {
    if (!lastAnalysisTime) return false;
    return (Date.now() - lastAnalysisTime) < CACHE_TTL;
  }, [lastAnalysisTime]);

  const isNewsAnalysisRecent = useCallback(() => {
    if (!lastNewsAnalysisTime) return false;
    return (Date.now() - lastNewsAnalysisTime) < CACHE_TTL;
  }, [lastNewsAnalysisTime]);

  return {
    // Existing health data
    healthData,
    loading,
    showModal,
    shouldAutoShow,
    rateLimitInfo, // ✅ FIXED: Renamed from usageInfo
    error,
    qualifiesForReport: qualifiesForReport(userProfile),
    
    // ✅ CHANGED: Main functions now open existing analysis instead of regenerating
    triggerReport: openAnalysis, // ✅ This now opens existing analysis
    triggerNewsAnalysis: openNewsAnalysis, // ✅ This now opens existing news analysis
    
    // ✅ NEW: Explicit refresh functions for the modal buttons
    refreshReport: triggerReport, // This generates new analysis
    refreshNewsAnalysis: triggerNewsAnalysis, // This generates new news analysis
    
    dismissModal,
    getHealthScoreColor,
    formatHealthForChat,
    clearError: () => setError(null),

    // News analysis data and functions
    newsData,
    newsLoading,
    newsError,
    newsRateLimitInfo,
    checkNewsUsage,
    formatNewsForChat,
    clearNewsError: () => setNewsError(null),

    // ✅ NEW: Analysis status helpers
    hasExistingAnalysis: !!healthData,
    hasExistingNewsAnalysis: !!newsData,
    isAnalysisRecent: isAnalysisRecent(),
    isNewsAnalysisRecent: isNewsAnalysisRecent(),
    lastAnalysisTime,
    lastNewsAnalysisTime,

    // Combined refresh function for convenience
    refreshData: useCallback(async () => {
      try {
        await generateNewReport();
        await checkNewsUsage();
      } catch (error) {
        console.error('Failed to refresh data:', error);
      }
    }, [generateNewReport, checkNewsUsage])
  };
};

export default usePortfolioHealth;